import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Grid,
  TextField,
  Typography,
  ListItemText,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Tooltip
} from '@material-ui/core';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { constants } from 'views/Campaign';
import Skeleton from 'react-loading-skeleton';
import LicenseCheck from 'LicenseCheck';
import { useDispatch, useSelector } from 'react-redux';
import { getAllLists, getAllSegmentedLists } from 'store/lists/actions';
import { Autocomplete } from '@material-ui/lab';
import { Info } from '@material-ui/icons';
import { SkeletonCardFields } from 'components/Loading';
import { ProductType } from 'constants.js';
import { getEnvVar } from 'helpers';
import { SelectInput } from 'components';
import {
  getCampaignAutomationOptions,
  getEventTypeOptions
} from 'helpers/templateHelper';

const {
  campaignBasicOccurrenceTypeOptions,
  campaignStandardOccurrenceTypeOptions,
  campaignOnDemandOccurrenceTypeOptions,
  campaignOccurrenceTypeOptions,
  campaignOccurrenceTypes
} = constants;

const {
  ON_DEMAND,
  ON_EVENT,
  SCHEDULED,
  INTERNAL_SURVEY_RESPONSE,
  EXTERNAL_SURVEY_RESPONSE
} = campaignOccurrenceTypes;

const useStyles = makeStyles(theme => ({
  inputGrid: {
    maxWidth: '40rem'
  },
  radioGroup: {
    paddingTop: '8px',
    marginLeft: '8px'
  },
  tooltip: {
    marginTop: '16px',
    marginLeft: '12px'
  },
  autocomplete: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
}));

function checkConditionQueryTemplate(
  isItineraryOnly,
  thisTemplate,
  type,
  useCampaignAutomation
) {
  return (
    (!isItineraryOnly() || type === 'queryTemplate' || !useCampaignAutomation) &&
    (thisTemplate.campaignOccurrenceType === SCHEDULED ||
      (((!useCampaignAutomation || thisTemplate.campaignOccurrenceType === ON_DEMAND) ||
        type === 'conditionQueryTemplate') &&
        thisTemplate.productType !== ProductType.List && 
        thisTemplate.productType !== ProductType.GUEST_INSIGHTS))
  );
}
function checkSurveyTemplate(isSurveyTemplate, thisTemplate) {
  return (
    (isSurveyTemplate ||
      (thisTemplate.campaignOccurrenceType &&
        thisTemplate.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) ||
      (thisTemplate.campaignOccurrenceType &&
        thisTemplate.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE)) &&
    thisTemplate.productType !== ProductType.List && 
    thisTemplate.productType !== ProductType.GUEST_INSIGHTS
  );
}

const TemplateAutomationOptions = props => {
  const {
    className,
    template,
    type,
    disabled,
    useCampaignAutomation = true,
    isPopupDialog,
    isSurveyTemplate,
    ...rest
  } = props;
  const {
    thisTemplate,
    handleChange,
    handleAutomationChange,
    handleAutoFillOption,
    onFocus,
    onBlur,
    getErrorMessage,
    shouldShowError,
    supportedScheduleTypeOptions,
    supportedOnDemandTypeOptions,
    supportedEventTypeOptions,
    productIntegrationOptions,
    allEventTypeCategoryOptions,
    supportedInternalSurveyResponseTypeOptions,
    supportedExternalSurveyResponseTypeOptions,
    isLoading,
    handleTypeChange,
    handleSubscriberListChange
  } = template;

  const licenses = LicenseCheck();
  const { hasFeatureStandard, hasFeatureOnDemand, hasFeatureSurvey } = licenses;

  const allLists = useSelector(store => store.lists.allLists.current);
  const allSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.current
  );
  const gettingAllLists = useSelector(store => store.lists.allLists.getting);
  const gettingAllSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.getting
  );

  const [
    templateNeedsUpdateFromUser,
    setTemplateNeedsUpdateFromUser
  ] = useState(false);
  const [subscribersList, setSubscribersList] = useState([]);

  const classes = useStyles();
  const dispatch = useDispatch();

  const optionsAreNotEditable = () =>
    disabled &&
    // these are here for backward compatibility - need to be removed soon
    !templateNeedsUpdateFromUser &&
    window.templateEventTypesAreEditable !== true;
  const isCopyFlow = !!thisTemplate?._isCopy || !!thisTemplate?._isCopyTemplate;

  const isItineraryOnly = () =>
    productIntegrationOptions &&
    productIntegrationOptions.length === 1 &&
    productIntegrationOptions[0] === 'itinerary';

  const handleChangeUsingKeyValuePair = changePairs => {
    let events = Object.keys(changePairs)
      .map(key => {
        if ((thisTemplate._isCopyTemplate && thisTemplate[key]) || thisTemplate[key] !== changePairs[key]) {
          return {
            target: {
              name: key,
              value: changePairs[key]
            }
          };
        }
      })
      .filter(e => !!e);
    events.length && handleChange(events);
  };

  useEffect(() => {
    let schemaId;
    if (thisTemplate.campaignOccurrenceType === SCHEDULED) {
      return handleChangeUsingKeyValuePair({
        schemaId: 'static_scheduled'
      });
    }

    if (thisTemplate.campaignOccurrenceType === ON_DEMAND) {
      return handleChangeUsingKeyValuePair({
        schemaId: 'static_onDemand'
      });
    }

    if (thisTemplate.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE) {
      return handleChangeUsingKeyValuePair({
        schemaId: ''
      });
    }

    if (thisTemplate.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) {
      return handleChangeUsingKeyValuePair({
        schemaId: ''
      });
    }

    if (
      thisTemplate.campaignOccurrenceType === ON_EVENT &&
      thisTemplate._eventType === ''
    ) {
      return handleChangeUsingKeyValuePair({
        schemaId: ''
      });
    }

    if (thisTemplate.schemaIdentifier !== '') {
      let option = supportedEventTypeOptions?.find(
        option => option.id === thisTemplate.schemaIdentifier
      );
      schemaId = option?.schemaId;
      return handleChangeUsingKeyValuePair({
        schemaId
      });
    }
  }, [
    thisTemplate.campaignOccurrenceType,
    thisTemplate.schemaIdentifier,
    thisTemplate._eventType,
    supportedEventTypeOptions
  ]);

  useEffect(() => {
    if (
      useCampaignAutomation &&
      type !== 'queryTemplate' &&
      isItineraryOnly() &&
      !thisTemplate.campaignOccurrenceType
    ) {
      handleAutoFillOption();
    }
  }, [useCampaignAutomation, thisTemplate.campaignOccurrenceType, productIntegrationOptions, type]);

  useEffect(() => {
    let options = [];
    if (!useCampaignAutomation) {
      options = (allEventTypeCategoryOptions || []).filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
    } else if (thisTemplate.campaignOccurrenceType === SCHEDULED) {
      options = (supportedScheduleTypeOptions || []).filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
    } else if (
      thisTemplate.campaignOccurrenceType === ON_DEMAND ||
      type === 'conditionQueryTemplate'
    ) {
      options = (supportedOnDemandTypeOptions || []).filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
    } else if (thisTemplate.campaignOccurrenceType === ON_EVENT) {
      options = (supportedEventTypeOptions || []).filter(
        option => option.type === 'EVENT_TYPE'
      );
    } else if (
      thisTemplate.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE
    ) {
      options = (supportedInternalSurveyResponseTypeOptions || []).filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
    } else if (
      thisTemplate.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE
    ) {
      options = (supportedExternalSurveyResponseTypeOptions || []).filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
    }

    if (thisTemplate.productType !== ProductType.List && thisTemplate.productType !== ProductType.GUEST_INSIGHTS) {
      if (options.length === 1) {
        handleAutomationChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      } else {
        if (
          !thisTemplate?._isCopy &&
          !thisTemplate.id &&
          !isPopupDialog &&
          (!thisTemplate.schemaIdentifier ||
            thisTemplate.schemaIdentifier === '')
        ) {
          handleAutomationChange({
            target: { name: 'schemaIdentifier', value: '' }
          });
        }
      }
    }
  }, [
    useCampaignAutomation,
    thisTemplate.campaignOccurrenceType,
    thisTemplate.productType,
    supportedOnDemandTypeOptions
  ]);

  useEffect(() => {
    if (
      thisTemplate.productType === ProductType.List &&
      allLists.length === 0 &&
      allSegmentedLists.length === 0
    ) {
      dispatch(getAllLists());
      dispatch(getAllSegmentedLists());
    }
  }, [thisTemplate.productType]);

  useEffect(() => {
    if (
      !gettingAllLists &&
      !gettingAllSegmentedLists &&
      (allSegmentedLists.length > 0 || allLists.length > 0)
    ) {
      let listsMap = {};
      allLists.map(list => {
        listsMap[list.id] = {
          ...list,
          searchText: list.name.toLowerCase(),
          children: []
        };
      });
      allSegmentedLists.map(segList => {
        let parentList = listsMap[segList.listId];
        parentList.searchText = `${
          parentList.searchText
        }.${segList.name.toLowerCase()}`;
        parentList.children = [
          ...parentList.children,
          { ...segList, searchText: segList.name.toLowerCase() }
        ];
      });
      let finalList = [];
      Object.values(listsMap).forEach(list => {
        finalList.push(list);
        if (list.children.length > 0) {
          const sortedList = [...list.children].sort((list1, list2) =>
            list1.name < list2.name ? -1 : 1
          );
          finalList.push(...sortedList);
        }
      });
      setSubscribersList(finalList);
    }
  }, [allSegmentedLists, allLists]);

  useEffect(() => {
    if (thisTemplate.id && !thisTemplate.schemaIdentifier) {
      setTemplateNeedsUpdateFromUser(true);
      return;
    }

    if (useCampaignAutomation && !thisTemplate.campaignOccurrenceType) {
      if (thisTemplate.schemaId === 'static_scheduled') {
        handleChangeUsingKeyValuePair({
          campaignOccurrenceType: SCHEDULED
        });
        return;
      }

      if (thisTemplate.schemaId === 'static_onDemand') {
        handleChangeUsingKeyValuePair({
          campaignOccurrenceType: ON_DEMAND
        });
        return;
      }
    }
  }, [useCampaignAutomation, thisTemplate]);

  useEffect(() => {
    if (
      !useCampaignAutomation &&
      thisTemplate.productType !== ProductType.List &&
      thisTemplate.productType !== ProductType.GUEST_INSIGHTS &&
      thisTemplate.schemaIdentifier &&
      `${thisTemplate.schemaIdentifier}`.split('.').length > 3
    ) {
      const [productCategory, productName, eventTypeCategory] = `${thisTemplate.schemaIdentifier}`.split('.');
      handleChangeUsingKeyValuePair({
        productCategory: productCategory || '',
        productName: productName || '',
        eventTypeCategory: eventTypeCategory || '',
        schemaIdentifier: [productCategory, productName, eventTypeCategory]
          .filter(Boolean)
          .join('.')
      });
    }
  }, [
    useCampaignAutomation,
    thisTemplate.productType,
    thisTemplate.schemaIdentifier
  ]);

  const handleOptionChanges = e => {
    const {
      target: { value, name }
    } = e;

    if (value === SCHEDULED) {
      handleChangeUsingKeyValuePair({
        campaignOccurrenceType: SCHEDULED,
        schemaId: thisTemplate._isCopy ? thisTemplate.schemaId : null,
        productName: thisTemplate._isCopy ? thisTemplate.productName : '',
        productCategory: thisTemplate._isCopy ? thisTemplate.productCategory : '',
        productType: ProductType.Product
      });

      return;
    }

    if (value === ON_DEMAND) {
      handleChangeUsingKeyValuePair({
        campaignOccurrenceType: ON_DEMAND,
        schemaId: null,
        productName: '',
        productCategory: '',
        productType: ProductType.Product
      });

      return;
    }

    if (value === INTERNAL_SURVEY_RESPONSE) {
      handleChangeUsingKeyValuePair({
        campaignOccurrenceType: INTERNAL_SURVEY_RESPONSE,
        schemaId: null,
        productName: '',
        productCategory: '',
        productType: ProductType.Product
      });

      return;
    }

    if (value === EXTERNAL_SURVEY_RESPONSE) {
      handleChangeUsingKeyValuePair({
        campaignOccurrenceType: EXTERNAL_SURVEY_RESPONSE,
        schemaId: null,
        productName: '',
        productCategory: '',
        productType: ProductType.Product
      });

      return;
    }

    if (value === ON_EVENT) {
      handleChangeUsingKeyValuePair({
        campaignOccurrenceType: ON_EVENT,
        schemaId: thisTemplate._isCopy ? thisTemplate.schemaId : null,
        productName: thisTemplate._isCopy ? thisTemplate.productName : '',
        productCategory: thisTemplate._isCopy ? thisTemplate.productCategory : '',
        productType: ProductType.Product
      });

      return;
    }
  };

  const SkeletonCardContent = () => (
    <Grid item md={6} xs={12}>
      <Typography
        display="block"
        style={{ fontSize: '2.2rem' }}
        className={classes.inputGrid}>
        <Skeleton />
      </Typography>
    </Grid>
  );

  const segListStyle = makeStyles(theme => ({
    root: {
      marginLeft: theme.spacing(1),
      color: 'grey'
    }
  }))();

  const filterSubscribersList = (options, state) => {
    let searchInput = state.inputValue.toLowerCase();
    return options.filter(option => option.searchText.includes(searchInput));
  };

  const getSubscribersListValue = () => {
    let selectedList = subscribersList.filter(list =>
      thisTemplate.segmentedListId && thisTemplate.segmentedListId !== ''
        ? list.id === thisTemplate.segmentedListId
        : list.id === thisTemplate.subscriberListId
    );
    return selectedList.length === 1 ? selectedList[0] : '';
  };

  const getSelectInputData = inputName => {
    return {
      fullWidth: true,
      required: true,
      margin: 'dense',
      onFocus: event => onFocus(event),
      onBlur: event => onBlur(event),
      error: shouldShowError(inputName),
      helperText: getErrorMessage(inputName)
    };
  };

  const normalizeSchemaIdentifier = value => {
    const schemaValue = `${value || ''}`;
    if (!schemaValue) {
      return '';
    }
    const segments = schemaValue.split('.');
    if (segments.length > 3) {
      return segments.slice(0, 3).join('.');
    }
    return schemaValue;
  };

  const getSchemaIdentifierValue = () => {
    if (!useCampaignAutomation) {
      return normalizeSchemaIdentifier(thisTemplate.schemaIdentifier);
    }
    return thisTemplate.schemaIdentifier || '';
  };

  const handleSchemaIdentifierChange = event => {
    if (!useCampaignAutomation) {
      const selectedValue = event?.target?.value;
      const selectedOption = allEventTypeCategoryOptions?.find(
        option =>
          option.id === selectedValue ||
          option.schemaIdentifier === selectedValue
      );
      handleAutomationChange({
        target: {
          ...event.target,
          value: normalizeSchemaIdentifier(
            selectedOption?.schemaIdentifier || selectedValue
          )
        }
      });
      return;
    }
    handleAutomationChange(event);
  };

  const isOptionEditableForTransient = () => {
    return supportedEventTypeOptions.filter(option =>
      (option.productName === thisTemplate.productName) &&
      (option.productCategory === thisTemplate.productCategory) &&
      (option.eventTypeCategory === thisTemplate.eventTypeCategory) &&
      (option.type === 'EVENT_TYPE'))
      .length > 1;
  };

  const disableTemplateEventTypes = () => {
    return ((thisTemplate?.hasOwnProperty('uuid') && !thisTemplate?.hasOwnProperty('isLongTermStorage')) ||
      (!thisTemplate?.isLongTermStorage && optionsAreNotEditable() && !isOptionEditableForTransient()));
  };

  const getSchemaOptionsList = () => {
    if (type === 'conditionQueryTemplate') {
      return allEventTypeCategoryOptions || [];
    }

    if (!useCampaignAutomation) {
      return allEventTypeCategoryOptions || [];
    }

    return productIntegrationOptions || [];
  };

  return (
    <div className={classes.root}>
      {!isPopupDialog && (
        <div>
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <Grid item xs={12}>
              {useCampaignAutomation && !isSurveyTemplate && props.type !== 'conditionQueryTemplate' && (
                <Grid item xs={12} md={6}>
                  <SelectInput
                    {...getSelectInputData('campaignOccurrenceType')}
                    label="Select the campaign Automation"
                    name="campaignOccurrenceType"
                    onChange={handleOptionChanges}
                    disabled={!(thisTemplate?.isLongTermStorage && thisTemplate?.isTypeEditable) && (
                      optionsAreNotEditable() ||
                      (type && type === 'queryTemplate')
                    )
                    }
                    value={thisTemplate.campaignOccurrenceType || ''}
                    options={getCampaignAutomationOptions({
                      campaignBasicOccurrenceTypeOptions,
                      campaignOccurrenceTypeOptions,
                      campaignOnDemandOccurrenceTypeOptions,
                      campaignStandardOccurrenceTypeOptions,
                      isItineraryOnly: isItineraryOnly(),
                      hasFeatureSurvey: hasFeatureSurvey(),
                      hasFeatureOnDemand: hasFeatureOnDemand(),
                      hasFeatureStandard: hasFeatureStandard(),
                      type,
                      currentTemplate: thisTemplate
                    })}
                  />
                </Grid>
              )}

              {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' &&
                hasFeatureOnDemand() &&
                (!useCampaignAutomation ||
                  thisTemplate.campaignOccurrenceType === 'ON_DEMAND' ||
                  type === 'conditionQueryTemplate' ||
                  ((thisTemplate.campaignOccurrenceType ===
                    'INTERNAL_SURVEY_RESPONSE' ||
                    thisTemplate.campaignOccurrenceType ===
                    'EXTERNAL_SURVEY_RESPONSE' ||
                    isSurveyTemplate) &&
                    getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_SURVEYS') ===
                      'true')) && (
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormControlLabel
                        label="Type:"
                        labelPlacement="start"
                        control={
                          <RadioGroup
                            className={classes.radioGroup}
                            aria-label="onDemand type"
                            name="productType"
                            value={
                              thisTemplate.productType || ProductType.Product
                            }
                            onChange={handleTypeChange}
                            row>
                            <FormControlLabel
                              value={ProductType.Product}
                              label="Product type"
                              disabled={isCopyFlow || optionsAreNotEditable()}
                              labelPlacement="end"
                              control={<Radio color="primary" />}
                            />
                            <FormControlLabel
                              value={ProductType.List}
                              label="Subscribers list"
                              disabled={isCopyFlow || optionsAreNotEditable()}
                              labelPlacement="end"
                              control={<Radio color="primary" />}
                            />
                            {thisTemplate.campaignOccurrenceType !==
                              'INTERNAL_SURVEY_RESPONSE' &&
                              thisTemplate.campaignOccurrenceType !==
                              'EXTERNAL_SURVEY_RESPONSE' &&
                              !isSurveyTemplate && (
                                <FormControlLabel
                                  value={ProductType.GUEST_INSIGHTS}
                                  label="Guest Insights"
                                  disabled={isCopyFlow || optionsAreNotEditable()}
                                  labelPlacement="end"
                                  control={<Radio color="primary" />}
                                />
                              )}
                          </RadioGroup>
                        }
                      />
                    </FormControl>
                    <Tooltip
                      title={`Choose 'Product Type' for selecting events from the enabled products (or) 
                      Choose 'Subscriber List' for selecting list${(getEnvVar(
                          'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
                            'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true') &&
                          '/segmented list'
                      ) || ''} of list management${(thisTemplate.campaignOccurrenceType !==
                        'INTERNAL_SURVEY_RESPONSE' &&
                        thisTemplate.campaignOccurrenceType !==
                        'EXTERNAL_SURVEY_RESPONSE' &&
                        !isSurveyTemplate) && ` (or)
                      Choose 'Guest Insights' to target guest profiles` || ''}`}>
                      <Info size="small" className={classes.tooltip} />
                    </Tooltip>
                  </Grid>
                )}

              {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') !== 'true' &&
                hasFeatureOnDemand() &&
                (!useCampaignAutomation ||
                  thisTemplate.campaignOccurrenceType === 'ON_DEMAND' ||
                  type === 'conditionQueryTemplate') && (
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormControlLabel
                        label="Type:"
                        labelPlacement="start"
                        control={
                          <RadioGroup
                            className={classes.radioGroup}
                            aria-label="onDemand type"
                            name="productType"
                            value={
                              thisTemplate.productType || ProductType.Product
                            }
                            onChange={handleTypeChange}
                            row>
                            <FormControlLabel
                              value={ProductType.Product}
                              label="Product type"
                              disabled={isCopyFlow || optionsAreNotEditable()}
                              labelPlacement="end"
                              control={<Radio color="primary" />}
                            />
                            <FormControlLabel
                              value={ProductType.GUEST_INSIGHTS}
                              label="Guest Insights"
                              disabled={isCopyFlow || optionsAreNotEditable()}
                              labelPlacement="end"
                              control={<Radio color="primary" />}
                            />
                          </RadioGroup>
                        }
                      />
                    </FormControl>
                    <Tooltip
                      title={`Choose 'Product Type' for selecting events from the enabled products(or)
                      Choose 'Guest Insights' to target guest profiles`}>
                      <Info size="small" className={classes.tooltip} />
                    </Tooltip>
                  </Grid>
                )}

              {useCampaignAutomation &&
                thisTemplate.campaignOccurrenceType &&
                thisTemplate.campaignOccurrenceType === ON_EVENT && (
                  <Grid item xs={12} md={6}>
                    <SelectInput
                      {...getSelectInputData('schemaIdentifier')}
                      label="Template Event Type"
                      name="schemaIdentifier"
                      onChange={handleAutomationChange}
                      disabled={disableTemplateEventTypes()}
                      value={thisTemplate.schemaIdentifier || ''}
                      selectProps={{
                        renderValue: option => option
                      }}
                      options={getEventTypeOptions(
                        supportedEventTypeOptions,
                        true,
                        thisTemplate
                      )}
                    />
                  </Grid>
                )}
              {useCampaignAutomation && checkSurveyTemplate(isSurveyTemplate, thisTemplate) && (
                <Grid item xs={12} md={6}>
                  <SelectInput
                    {...getSelectInputData('schemaIdentifier')}
                    label="Template Event Type"
                    name="schemaIdentifier"
                    onChange={handleAutomationChange}
                    disabled={isCopyFlow || optionsAreNotEditable()}
                    value={thisTemplate.schemaIdentifier || ''}
                    selectProps={{
                      renderValue: option => option
                    }}
                    options={getEventTypeOptions(
                      supportedInternalSurveyResponseTypeOptions
                    )}
                  />
                </Grid>
              )}

              {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' &&
                (!useCampaignAutomation ||
                  thisTemplate.campaignOccurrenceType === ON_DEMAND ||
                  type === 'conditionQueryTemplate' ||
                  ((thisTemplate.campaignOccurrenceType ===
                    INTERNAL_SURVEY_RESPONSE ||
                    thisTemplate.campaignOccurrenceType ===
                    EXTERNAL_SURVEY_RESPONSE ||
                    isSurveyTemplate) &&
                    getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_SURVEYS') ===
                      'true')) &&
                thisTemplate.productType === ProductType.List && (
                  <React.Fragment>
                    {gettingAllLists || gettingAllSegmentedLists ? (
                      <SkeletonCardFields numberOfRows={1} />
                    ) : (
                      <Grid item xs={12} className={classes.autocomplete}>
                        <Grid item md={6}>
                          <Autocomplete
                            id="size-small-outlined"
                            size="small"
                            disabled={optionsAreNotEditable() && (!thisTemplate?._isCopy || !!(thisTemplate?.uuid || thisTemplate?.id))}
                            value={getSubscribersListValue()}
                            onChange={handleSubscriberListChange}
                            options={subscribersList || []}
                            getOptionLabel={option => option.name}
                            filterOptions={(options, state) =>
                              filterSubscribersList(options, state)
                            }
                            renderOption={option => (
                              <React.Fragment>
                                {option.listId ? (
                                  <React.Fragment>
                                    <SubdirectoryArrowRightIcon
                                      classes={segListStyle}
                                    />
                                    <ListItemText>{option.name}</ListItemText>
                                  </React.Fragment>
                                ) : (
                                  <ListItemText>{option.name}</ListItemText>
                                )}
                              </React.Fragment>
                            )}
                            renderInput={params => (
                              <TextField
                                {...params}
                                margin="dense"
                                label="Select a subscriber list"
                                variant="outlined"
                                error={shouldShowError('subscriberListId')}
                                helperText={getErrorMessage('subscriberListId')}
                              />
                            )}
                          />
                        </Grid>
                        {(getEnvVar(
                          'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
                            'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) && (
                          <Tooltip title="This template will be created for the parent list and can be used by all its segmented lists.">
                            <Info
                              size="small"
                              style={{ marginTop: '8px', marginLeft: '12px' }}
                            />
                          </Tooltip>
                        )}
                      </Grid>
                    )}
                  </React.Fragment>
                )}
              {checkConditionQueryTemplate(
                isItineraryOnly,
                thisTemplate,
                type,
                useCampaignAutomation
              ) && (
                <Grid item xs={12} md={6}>
                  <SelectInput
                    {...getSelectInputData('productName')}
                    label="Product type"
                    name="schemaIdentifier"
                    onChange={handleSchemaIdentifierChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={
                      isCopyFlow ||
                      (!thisTemplate?.isLongTermStorage && optionsAreNotEditable())
                    }
                    value={getSchemaIdentifierValue()}
                    selectProps={{
                      renderValue: option => option
                    }}
                    options={getEventTypeOptions(
                      getSchemaOptionsList(),
                        false,
                        thisTemplate
                    )}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateAutomationOptions;
